import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { Input, Popover, Tabs } from 'antd';
import cx from 'classnames';
import type { CSSProperties, FC, ReactNode } from 'react';
import './index.less';
import { getNodeChildren } from './path-fns';
import type { ICascaderItem, IProps } from './types';
import useCascader from './useCascader';

const { TabPane } = Tabs;
const baseClass = 'beukay-cascader';

export interface TabsCascaderProps extends IProps {
  placeholder?: string;
  title?: string[];
  style?: CSSProperties;
  className?: string;
  inputClassName?: string;
  popoverClassName?: string;
  showArrow?: boolean;
  allowClear?: boolean;
  footer?: ReactNode;
}

const TabsCascader: FC<TabsCascaderProps> = ({
  title = ['请选择', '请选择', '请选择', '请选择'],
  style,
  placeholder = '请选择',
  className,
  popoverClassName,
  showArrow = true,
  allowClear = true,
  footer,
  inputClassName,
  ...restProps
}) => {
  const {
    options,
    value,
    visible,
    setVisible,
    onSelect,
    activeTab,
    setActiveTab,
    valueText,
    triggerRef,
    isHovering,
    onClear,
  } = useCascader(restProps);

  const renderCascaderItems = (nodes: ICascaderItem[], level: number) => {
    const val = value[level - 1];
    return nodes.map((node) => (
      <div
        key={node.adCode}
        className={cx(`${baseClass}-item`, { active: node.adCode === val })}
        onClick={() => onSelect(node)}
      >
        {node.name}
      </div>
    ));
  };

  const renderTabPanes = () => {
    const panes = [];
    const maxLevel = value.length + 1;

    for (
      let i = 0, opts: ICascaderItem[] | null | undefined = options;
      i < maxLevel;
      i++, opts = getNodeChildren(opts, value[i - 1])
    ) {
      if (opts && opts.length > 0) {
        const val = value[i];
        const selectedItem = opts.find((n) => n.adCode === val);
        const tabTitle = selectedItem?.name ?? title[i];
        const level = i + 1;

        panes.push(
          <TabPane
            className={`${baseClass}-card`}
            tab={tabTitle}
            key={String(level)}
          >
            {renderCascaderItems(opts, level)}
          </TabPane>,
        );
      }
    }

    return panes;
  };

  const content = (
    <div className={`${baseClass}-card-wrapper`}>
      <Tabs
        activeKey={String(activeTab)}
        type="card"
        size="small"
        onChange={(activeTab) => {
          setActiveTab(Number(activeTab));
        }}
      >
        {renderTabPanes()}
      </Tabs>
      {footer}
    </div>
  );

  const getSuffix = () => {
    if (isHovering && value?.length && allowClear) {
      return (
        <CloseCircleFilled
          onClick={onClear}
          className={`${baseClass}-selector-clear-icon`}
        />
      );
    }
    if (showArrow) {
      return <DownOutlined className={`${baseClass}-selector-arrow-icon`} />;
    }
    return null;
  };

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      className={cx(`${baseClass}-popover`, popoverClassName)}
      content={content}
      open={visible}
      overlayClassName={`${baseClass}-overlay`}
      onOpenChange={setVisible}
    >
      <div
        style={style}
        ref={triggerRef}
        className={cx(`${baseClass}-selector`, className)}
      >
        <Input
          readOnly
          className={inputClassName}
          type="search"
          suffix={getSuffix()}
          placeholder={placeholder}
          value={valueText}
        />
      </div>
    </Popover>
  );
};

export default TabsCascader;
